import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  Clock,
  User,
  Package,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Calendar,
  FileText,
  History,
} from 'lucide-react';
import { meetingsApi, contactsApi } from '../services/api';
import { MeetingStatus } from '../types';
import StatusBadge from '../components/ui/StatusBadge';
import toast from 'react-hot-toast';

export default function MeetingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  // Fetch meeting details
  const { data: meeting, isLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.getMeeting(id!),
    enabled: !!id,
  });

  // Fetch contact meeting history
  const { data: contactMeetings = [] } = useQuery({
    queryKey: ['contact-meetings', meeting?.contactId],
    queryFn: () => contactsApi.getContactMeetings(meeting!.contactId),
    enabled: !!meeting?.contactId,
  });

  // Update meeting status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: MeetingStatus }) =>
      meetingsApi.updateMeetingStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Status atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar status');
    },
  });

  // Save notes mutation
  const saveNotesMutation = useMutation({
    mutationFn: (notes: string) => meetingsApi.addMeetingNotes(id!, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      setIsEditingNotes(false);
      toast.success('Notas salvas com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao salvar notas');
    },
  });

  const handleStatusChange = (status: MeetingStatus) => {
    if (window.confirm(`Confirmar mudança de status?`)) {
      updateStatusMutation.mutate({ status });
    }
  };

  const handleSaveNotes = () => {
    if (notes.trim()) {
      saveNotesMutation.mutate(notes);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-600">Agendamento não encontrado</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg tap-target"
        >
          Voltar
        </button>
      </div>
    );
  }

  const scheduledTime = new Date(meeting.startTime);
  const pastMeetings = contactMeetings
    .filter((m) => m.id !== meeting.id && m.status === MeetingStatus.COMPLETED)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 safe-top">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700 tap-target"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 flex-1">Detalhes</h1>
          <StatusBadge status={meeting.status} size="md" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Cliente</h2>
          <div className="space-y-2">
            <div className="flex items-center text-gray-700">
              <User className="w-5 h-5 mr-3 text-gray-400" />
              <span>{meeting.contact?.name || '—'}</span>
            </div>
            {meeting.contact?.phone && (
              <div className="flex items-center text-gray-700">
                <Phone className="w-5 h-5 mr-3 text-gray-400" />
                <a href={`tel:${meeting.contact.phone}`} className="text-primary-600 hover:underline">
                  {meeting.contact.phone}
                </a>
              </div>
            )}
            {meeting.contact?.email && (
              <div className="flex items-center text-gray-700">
                <Mail className="w-5 h-5 mr-3 text-gray-400" />
                <a href={`mailto:${meeting.contact.email}`} className="text-primary-600 hover:underline">
                  {meeting.contact.email}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Meeting Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Agendamento</h2>
          <div className="space-y-2">
            <div className="flex items-center text-gray-700">
              <Calendar className="w-5 h-5 mr-3 text-gray-400" />
              <span>{format(scheduledTime, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
            </div>
            <div className="flex items-center text-gray-700">
              <Clock className="w-5 h-5 mr-3 text-gray-400" />
              <span>{format(scheduledTime, 'HH:mm', { locale: ptBR })} ({meeting.duration} min)</span>
            </div>
            {meeting.product && (
              <div className="flex items-center text-gray-700">
                <Package className="w-5 h-5 mr-3 text-gray-400" />
                <span>{meeting.product.name}</span>
              </div>
            )}
            {meeting.user && (
              <div className="flex items-center text-gray-700">
                <User className="w-5 h-5 mr-3 text-gray-400" />
                <span>Operador: {meeting.user.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {meeting.status !== MeetingStatus.COMPLETED && meeting.status !== MeetingStatus.CANCELED && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ações</h2>
            <div className="grid grid-cols-2 gap-3">
              {meeting.status === MeetingStatus.SCHEDULED && (
                <button
                  onClick={() => handleStatusChange(MeetingStatus.CONFIRMED)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 tap-target"
                >
                  <CheckCircle className="w-5 h-5" />
                  Confirmar
                </button>
              )}
              {(meeting.status === MeetingStatus.CONFIRMED || meeting.status === MeetingStatus.IN_PROGRESS) && (
                <button
                  onClick={() => handleStatusChange(MeetingStatus.COMPLETED)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 tap-target"
                >
                  <CheckCircle className="w-5 h-5" />
                  Concluir
                </button>
              )}
              <button
                onClick={() => handleStatusChange(MeetingStatus.CANCELED)}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 tap-target"
              >
                <XCircle className="w-5 h-5" />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Notas
            </h2>
            {!isEditingNotes && (
              <button
                onClick={() => {
                  setNotes(meeting.notes || '');
                  setIsEditingNotes(true);
                }}
                className="text-primary-600 text-sm font-medium tap-target"
              >
                {meeting.notes ? 'Editar' : 'Adicionar'}
              </button>
            )}
          </div>

          {isEditingNotes ? (
            <div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Adicione notas sobre o atendimento..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
                rows={4}
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleSaveNotes}
                  disabled={saveNotesMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 tap-target disabled:bg-gray-300"
                >
                  {saveNotesMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  onClick={() => setIsEditingNotes(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 tap-target"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : meeting.notes ? (
            <p className="text-gray-700 whitespace-pre-wrap">{meeting.notes}</p>
          ) : (
            <p className="text-gray-500 italic">Nenhuma nota adicionada</p>
          )}
        </div>

        {/* Meeting History */}
        {pastMeetings.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico do Cliente
            </h2>
            <div className="space-y-2">
              {pastMeetings.map((pastMeeting) => (
                <div
                  key={pastMeeting.id}
                  className="p-3 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">
                      {format(new Date(pastMeeting.startTime), 'd/MM/yyyy', { locale: ptBR })}
                    </span>
                    <StatusBadge status={pastMeeting.status} size="sm" />
                  </div>
                  {pastMeeting.product && (
                    <p className="text-gray-600">{pastMeeting.product.name}</p>
                  )}
                  {pastMeeting.notes && (
                    <p className="text-gray-500 mt-1 line-clamp-2">{pastMeeting.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
